/** Entity selections affect their object/row and descendants within this form session. */
export class TimeZoneContext {
	private readonly values: Map<string, string>;

	private readonly sources = new Map<string, string>();

	public constructor(values?: Iterable<[string, string]>) {
		this.values = new Map(values);
	}

	public get(path: string): string | undefined {
		let current = path;

		while(current !== "") {
			const value = this.values.get(current);

			if(value !== undefined) {
				return value;
			}

			const separator = current.lastIndexOf(".");
			current = separator === -1 ? "" : current.slice(0, separator);
		}

		return this.values.get("");
	}

	public set(path: string, value?: string, sourcePath?: string): boolean {
		if(value === undefined && sourcePath !== undefined && this.sources.get(path) !== sourcePath) {
			return false;
		}

		if(this.values.get(path) === value && this.sources.get(path) === sourcePath) {
			return false;
		}

		if(value === undefined) {
			this.values.delete(path);
			this.sources.delete(path);

			return true;
		}

		this.values.set(path, value);

		if(sourcePath === undefined) {
			this.sources.delete(path);
		} else {
			this.sources.set(path, sourcePath);
		}

		return true;
	}

	public clearHidden(isVisible: (sourcePath: string) => boolean): boolean {
		let changed = false;

		this.sources.forEach((sourcePath, path) => {
			if(!isVisible(sourcePath)) {
				this.values.delete(path);
				this.sources.delete(path);
				changed = true;
			}
		});

		return changed;
	}

	public getOwn(path: string): string | undefined {
		return this.values.get(path);
	}

	public clone(): TimeZoneContext {
		const clone = new TimeZoneContext(this.values);
		this.sources.forEach((sourcePath, path) => clone.sources.set(path, sourcePath));

		return clone;
	}

	public replaceSubtree(path: string, source: TimeZoneContext): void {
		const inSubtree = (value?: string): boolean => value !== undefined
			&& (path === "" || value === path || value.startsWith(`${path}.`));

		this.values.forEach((_value, key) => {
			if(inSubtree(key) || inSubtree(this.sources.get(key))) {
				this.values.delete(key);
				this.sources.delete(key);
			}
		});

		source.values.forEach((value, key) => {
			if(inSubtree(key) || inSubtree(source.sources.get(key))) {
				this.values.set(key, value);
				const sourcePath = source.sources.get(key);

				if(sourcePath !== undefined) {
					this.sources.set(key, sourcePath);
				}
			}
		});
	}

	public removeRow(path: string, removedIndex: number): void {
		const prefix = path ? `${path}.` : "";
		const reindex = (value: string): string | undefined => {
			if(!value.startsWith(prefix)) {
				return value;
			}

			const [index, ...remaining] = value.slice(prefix.length).split(".");
			const row = Number(index);

			if(!/^\d+$/.test(index) || row < removedIndex) {
				return value;
			}

			return row === removedIndex ? undefined : `${prefix}${[row - 1, ...remaining].join(".")}`;
		};
		const entries = [...this.values.entries()];
		const sources = new Map(this.sources);
		this.values.clear();
		this.sources.clear();

		for(const [key, value] of entries) {
			const nextPath = reindex(key);
			const sourcePath = sources.get(key);
			const nextSource = sourcePath === undefined ? undefined : reindex(sourcePath);

			if(nextPath !== undefined && (sourcePath === undefined || nextSource !== undefined)) {
				this.set(nextPath, value, nextSource);
			}
		}
	}
}
